import { Col, message, Row } from "antd";
import { useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import Table from "@components/Table";
import Button from "@components/Button";
import { getColumns } from "./config";
import AddLongTermPlan from "./AddLongTermPlan";
import {
  ErrorServices,
  useDeleteLongTermPlanMutation,
  useFetchLongTermPlansQuery,
} from "@services";
import { ILongTermPlan } from "@types";
import { paths, permissions } from "@router";
import { useTypedSelector } from "@hooks";
import { checkPermission } from "@/utils";
import CustomButton from "@/components/customButton";
import { MdOutlineFileDownload } from "react-icons/md";
import SearchBox from "@/components/searchInput";
import { Dropdown } from "antd";
import { IoFilterSharp } from "react-icons/io5";
import useDebounce from "@/hooks/useDebounce";

const LongTermPlans = () => {
  const { company, role } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const history = useHistory();
  const longTermPlan_id = useRef<any>(null);
  const [page, setPage] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isVisibleForEdit, setIsVisibleForEdit] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebounce(searchInput, 500);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [order, setOrder] = useState<"asc" | "desc" | null>(null);


  const [selectedLongTermPlan, setSelectedLongTermPlan] =
    useState<ILongTermPlan | null>(null);
  const [deleteLongTermPlan, { isLoading: isDeleting }] =
    useDeleteLongTermPlanMutation();
  const { data: plansData, isLoading } = useFetchLongTermPlansQuery({
    company_id,
    page,
    page_size: pageSize,
    sort_by: sortBy,
    order: order,
    search: debouncedSearch,
  });
  const { data, pagination } = plansData || {};

  const onRowClick = (data: any) => {
    // history.push(`${window.location.pathname}/${data?.id}`);
    return;
  };

  const removeLongTermPlan = async (id: number) => {
    try {
      longTermPlan_id.current = id;
      await deleteLongTermPlan({ company_id, id }).unwrap();
      message.success("Long term incentive plan deleted successfully!");
    } catch (error) {
      ErrorServices(error);
    }
  };

  const editLongTermPlan = (selectedLongTermPlan: ILongTermPlan) => {
    setSelectedLongTermPlan(selectedLongTermPlan);
    setIsVisibleForEdit(true);
  };

  const columns = getColumns({
    isDeleting,
    longTermPlan_id,
    removeLongTermPlan,
    editLongTermPlan,
    userRole: role,
  });

  // ✅ COLUMN STATE
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(
    columns.map(col => col.key)
  );

  // ✅ FILTERED COLUMNS
  const filteredColumns = columns.filter(col =>
    visibleColumnKeys.includes(col.key)
  );

  // custom overlay
  const columnOverlay = (
    <div
      style={{
        background: "#fff",
        padding: 8,
        borderRadius: 6,
        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
        minWidth: 240,
        maxHeight: 300,
        overflowY: "auto",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {columns.map((col) => {
        const isActionsColumn = col.key === "actions";
        const isChecked = visibleColumnKeys.includes(col.key);
        const isDisabled =
          isActionsColumn || (isChecked && visibleColumnKeys.length <= 3);

        return (
          <label
            key={col.key}
            style={{
               display: isDisabled ? "none" : "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 8px",
              borderRadius: 4,
              cursor: isDisabled ? "not-allowed" : "pointer",
              opacity: isDisabled ? 0.6 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={isChecked}
              disabled={isDisabled}
              onChange={() => {
                if (isActionsColumn) return;

                if (isChecked) {
                  setVisibleColumnKeys((prev) =>
                    prev.filter((key) => key !== col.key)
                  );
                } else {
                  setVisibleColumnKeys((prev) => [...prev, col.key]);
                }
              }}
              style={{
                margin: 0,
                width: 16,
                height: 16,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                flex: 1,
                fontSize: 14,
                lineHeight: "18px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {typeof col.title === "string"
                ? col.title.replace(/\b\w/g, c => c.toUpperCase())
                : col.title}
            </span>
          </label>
        );
      })}
    </div>
  );


  return (
    <>
      {isVisible && (
        <AddLongTermPlan
          selectedLongTermPlan={selectedLongTermPlan}
          setSelectedLongTermPlan={setSelectedLongTermPlan}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
        />
      )}
      {isVisibleForEdit && (
        <AddLongTermPlan
          selectedLongTermPlan={selectedLongTermPlan}
          setIsVisible={() => {
            setIsVisibleForEdit(false);
            setSelectedLongTermPlan(null);
          }}
          setSelectedLongTermPlan={setSelectedLongTermPlan}
          isVisible={isVisibleForEdit}
          isEdit={true}
        />
      )}

      <div className="bg-white p-4 sm:p-5">
        <div className="jobCreationBar bg-revampBlue px-4 py-6 rounded-lg flex justify-between items-center ">
          <div className="title text-white  font-medium text-3xl uppercase">
            Long Term Incentive Plans
          </div>

          <div className="flex flex-row justify-between">
            <CustomButton
              onClick={() => history.push(paths.client.long_term_ip.incentives)}
              btnName="LTI Data"
              customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder"
              iconPosition="left"
            ></CustomButton>

            {checkPermission(permissions.CREATE_COMPANY_LTI) && (
              <CustomButton
                onClick={() => setIsVisible(true)}
                btnName="Create LTI Plan"
                customClasses="shadow-md font-medium border py-1 px-6 !border-themeBorder ml-4"
                iconPosition="right"
              >
                {/* <MdOutlineFileDownload
                  size={20}
                  className="text-white font-bold"
                /> */}
              </CustomButton>
            )}

            <SearchBox
              placeholder="Type to search..."
              value={searchInput}
              onChange={value => {
                setSearchInput(value);
                setPage(1);
              }}
            />
            <div className="dropdown ml-4">
              <Dropdown
                overlay={columnOverlay}
                trigger={["click"]}
                placement="bottomLeft"
              // getPopupContainer={trigger => trigger.parentElement}
              >
                <CustomButton
                  btnName="Column Filter"
                  customClasses="shadow-md font-medium border py-1 px-4"
                >
                 <IoFilterSharp />
                </CustomButton>
              </Dropdown>
            </div>
          </div>
        </div>
      </div>


      <div className="jobCreationListing mt-10 bg-white px-5 py-4 rounded-lg">

        <Row>
          <Table
            scroll={1800}
            data={data}
            columns={filteredColumns}
            isLoading={isLoading}
            pagination={true}
            count={pagination?.count}
            onChangePage={setPage}
            page={page}
            onRowClick={onRowClick}
            onPageSizeChange={setPageSize}
            onTableChange={(pagination, filters, sorter) => {
              console.log(sorter)
              if (sorter?.field) {
                setSortBy(sorter.field);
                setOrder(sorter.order === "ascend" ? "asc" : "desc");
                setPage(1);
              }
            }}
          />
        </Row>
      </div>
    </>
  );
};

export default LongTermPlans;
